import type { ComponentType, ReactElement, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Outlet, useRouteError, type RouteObject } from 'react-router';
import NotFoundPage from 'components/pages/not-found';
import AppRoute from 'components/routes/app-route';
import WebsiteLogin from 'components/pages/login/website-login';
import LoginPage from 'components/pages/login/login-page';
import LogoutPage from 'components/pages/login/logout-page';
import { invitationRoutes, onboardingRoutes, routes } from 'components/routes/routes';
import MainLayout from 'components/layout/main-layout';
import InnerLayout from 'components/layout/inner-layout';
import FaviconSwitcher from 'components/common/favicon-switcher';
import ErrorContainer from 'components/common/error-container';
import Loader from 'components/common/loader';

export const pageModules = import.meta.glob<{ default: ComponentType }>([
  './pages/**/*.tsx',
  '!./pages/**/*.stories.tsx',
  '!./pages/**/*.spec.tsx',
  '!./pages/**/*.test.tsx'
]);

export const RouteError = () => <ErrorContainer errorToReport={useRouteError()} />;

const lazyPage = (page: string, render: (Page: ComponentType) => ReactElement): Pick<RouteObject, 'lazy'> => {
  const loader = pageModules[`./pages/${page}.tsx`];
  if (!loader) throw new Error(`Page module not found: ${page}`);
  return {
    lazy: async () => {
      const { default: Page } = await loader();
      return { Component: () => render(Page) };
    }
  };
};

const PageLayout = ({ route, children }: { route: (typeof routes)[number]; children: ReactNode }) => {
  const intl = useIntl();
  return (
    <InnerLayout
      title={intl.formatMessage({ id: route.title })}
      breadcrumbs={route.breadcrumbs}
      container={route.container}>
      <FaviconSwitcher key={route.path} />
      {children}
    </InnerLayout>
  );
};

const NotFoundRoute = () => {
  const intl = useIntl();
  return (
    <AppRoute LazyComponent={NotFoundPage}>
      {content => <InnerLayout title={intl.formatMessage({ id: 'not-found.title' })}>{content}</InnerLayout>}
    </AppRoute>
  );
};

export const createAppRoutes = (): RouteObject[] => [
  {
    HydrateFallback: () => <Loader size="large" />,
    ErrorBoundary: RouteError,
    children: [
      // Authentication callbacks must render without waiting for a page module.
      { path: '/login/website', element: <AppRoute LazyComponent={WebsiteLogin} isPublic /> },
      { path: '/login', element: <AppRoute LazyComponent={LoginPage} isPublic /> },
      // Where Auth0 sends an IdP-initiated SSO login: every connection provisioned through the
      // self-serve flow carries `redirect_uri=<app>/login/sso` (common/auth0/src/auth0-api.ts), and
      // editing one no longer rewrites it. So this path keeps arriving from Okta however long ago the
      // login page behind it was retired, and it cannot be dropped until those connections are.
      { path: '/login/sso', element: <AppRoute LazyComponent={LoginPage} isPublic /> },
      { path: '/logout', element: <AppRoute LazyComponent={LogoutPage} isPublic /> },
      ...invitationRoutes.map(({ layout: Layout, ...route }) => ({
        path: route.path,
        handle: route,
        ...lazyPage(route.page, Page => (
          <AppRoute LazyComponent={Page} isPublic>
            {content => <Layout>{content}</Layout>}
          </AppRoute>
        ))
      })),
      ...onboardingRoutes.map(({ layout: Layout, ...route }) => ({
        path: route.path,
        handle: route,
        ...lazyPage(route.page, Page => (
          <AppRoute LazyComponent={Page}>{content => <Layout>{content}</Layout>}</AppRoute>
        ))
      })),
      {
        element: (
          <MainLayout>
            <Outlet />
          </MainLayout>
        ),
        children: [
          ...routes.map(route => {
            const render = (Page: ComponentType) => (
              <AppRoute
                LazyComponent={Page}
                rbacPermissions={route.requiredPermissions}
                permissionOperator={route.permissionOperator}
                permissionProjectScope={route.permissionProjectScope}>
                {content => <PageLayout route={route}>{content}</PageLayout>}
              </AppRoute>
            );
            return {
              // The boundary stays above the lazy route so a failed import cannot hide it.
              errorElement: render(RouteError),
              children: [{ path: route.path, handle: route, ...lazyPage(route.page, render) }]
            };
          }),
          { path: '*', Component: NotFoundRoute }
        ]
      }
    ]
  }
];
